import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import AddIcon from '@mui/icons-material/Add'
import {fetchEwsReps, persistEwsReps} from 'module/SessionInfo/api/EwsRepAPI';
import {doAfterNextSessionInfoRefreshDone, forceRefreshSessionInfo} from 'module/SessionInfo/api/SessionInfoAPI';
import {useSelector} from 'react-redux';
import {EwsRepSelect} from 'module/SessionInfo/component/userInfo/EwsRepSelect';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {showEwsFwdQuestion} from 'module/SessionInfo/util/showEwsFwdQuestion';
import {PropID} from 'module/Properties/enum/PropID';
import {selPropGen} from 'module/Properties/selector/selPropGen';
import {EwsRepTO} from 'generated/JavaTO';
import {serverErrorHandler} from 'module/ConfirmDialog/component/ConfirmDialogContainer';
import {css} from '@emotion/react'

function pickPersistableAttrs(x: EwsRepTO) {
	return _.pick(x, ['repAddress', 'useFwdRule']);
}

const cssListItem = css`
	width: 100%;
`
const cssButtonContainer = css`
	text-align: center;
`

export const EwsRepSelectList = React.memo<{ ewsRepList: EwsRepTO[] }>(({
	ewsRepList: savedEwsReps = [],
}) => {
	const maxEwsReps = useSelector(selPropGen(PropID.evoApps_mailVertretungMaxAmount, p => p && parseInt(p) || 0))
	const allowFwd = useSelector(selPropGen(PropID.evoApps_mailVertretungAllowFwd, p => p !== 'N'))
	const useGlobalFwdSwitch = useSelector(selPropGen(PropID.evoApps_mailVertretungGlobalFwdSwitch, p => p === 'J'))

	const disableSingleFwd = maxEwsReps < 1 || !allowFwd || useGlobalFwdSwitch;
	const [loading, setLoading] = useState(false);
	const [ewsRepList, setEwsRepList] = useState<Partial<EwsRepTO>[]>(savedEwsReps);
	const showAddBtn = maxEwsReps > ewsRepList.length;
	useEffect(() => setEwsRepList(savedEwsReps), [savedEwsReps]);
	const addRep = useCallback(() => setEwsRepList(r => r.concat({})), []);
	useEffect(() => {
		const t = setTimeout(() => {
			const toSave = ewsRepList.filter(x => !_.isEmpty(x.repAddress)).map(pickPersistableAttrs);
			const persisted = savedEwsReps.map(pickPersistableAttrs);
			if (!_.isEqual(toSave, persisted)) {
				setLoading(true);
				persistEwsReps(toSave).catch(serverErrorHandler).then(() => {
					doAfterNextSessionInfoRefreshDone(() => setLoading(false));
					forceRefreshSessionInfo();
				});
			}
		}, 1100);
		return () => clearTimeout(t);
	}, [savedEwsReps, ewsRepList]);

	const [options, setOptions] = useState<EwsRepTO[]>([]);
	useEffect(() => {
		fetchEwsReps().then(resp => setOptions(resp.data)).catch(serverErrorHandler);
	}, []);
	const otherOpts = useMemo(() => options.filter(o => !_.includes(ewsRepList.map(x => x.repAddress), o.repAddress)), [options, ewsRepList]); //todo filter???
	const onChangeUseGlobalFwdRule = useCallback((evt, val) => {
		if (val) {
			showEwsFwdQuestion().then(
				() => setEwsRepList(list => list.map(v => ({...v, useFwdRule: true}))),
			).catch(
				() => setEwsRepList(list => list.map(v => ({...v, useFwdRule: false}))),
			)
		} else {
			setEwsRepList(list => list.map(v => ({...v, useFwdRule: false})))
		}
	}, []);
	return (
		<Fragment>
			{ewsRepList.map((e, index) => (
					<div key={index} css={cssListItem}>
						<EwsRepSelect
							loading={loading}
							options={otherOpts}
							disabledFwd={disableSingleFwd}
							ewsRep={e}
							index={index}
							setEwsRepList={setEwsRepList}
						/>
					</div>
				),
			)}
			{showAddBtn &&
				<div css={[cssListItem, cssButtonContainer]}>
					<Button disabled={loading} onClick={addRep}><AddIcon/> Vertreter hinzufügen</Button>
				</div>
			}
			{allowFwd && useGlobalFwdSwitch &&
				<div css={cssListItem}>
					<FormControlLabel
						label={'Weiterleitungsregel bei Abwesenheit für alle Vertreter einrichten'}
						disabled={loading}
						control={
							<Checkbox
								checked={_.some(ewsRepList, x => x.useFwdRule)}
								onChange={onChangeUseGlobalFwdRule}
							/>
						}
					/>
				</div>
			}
		</Fragment>
	);
});